/**
 * /tools/good-day-fallback-day?clientId=X — Step 7 tool.
 *
 * Two-paragraph capture: what a "Good Day" looks like (full system on)
 * vs a "Fallback Day" (minimum viable cadence on the worst days). The
 * client picks the lighter day-type during daily tracking from Step 7
 * onward; this tool persists the canonical text the dashboard surfaces.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

interface FormState {
  goodDay: string;
  fallbackDay: string;
  reflection: string;
}

const DEFAULT_FORM: FormState = { goodDay: '', fallbackDay: '', reflection: '' };

export function GoodDayFallbackDayPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(
          accessToken,
          clientId,
          'good-day-fallback-day',
        );
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = { ...DEFAULT_FORM };
          if (typeof p.goodDay === 'string') next.goodDay = p.goodDay;
          if (typeof p.fallbackDay === 'string') next.fallbackDay = p.fallbackDay;
          if (typeof p.reflection === 'string') next.reflection = p.reflection;
          setForm(next);
          setSavedAt(existing.completed_at ?? null);
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const canSubmit =
    !submitting &&
    hydrated &&
    form.goodDay.trim().length > 0 &&
    form.fallbackDay.trim().length > 0;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.goodDayFallbackDay.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const payload = {
      goodDay: form.goodDay.trim(),
      fallbackDay: form.fallbackDay.trim(),
      ...(form.reflection.trim() ? { reflection: form.reflection.trim() } : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'good-day-fallback-day',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.goodDayFallbackDay.errors.generic'));
      } else {
        setSubmitError(t('tools.goodDayFallbackDay.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section
        className="max-w-2xl mx-auto px-s5 py-s8"
        data-testid="good-day-fallback-day-missing-client"
      >
        <h1 className="font-display text-display-lg text-ink">
          {t('tools.goodDayFallbackDay.title')}
        </h1>
        <p className="mt-s3 text-flame-ember">
          {t('tools.goodDayFallbackDay.errors.missingClient')}
        </p>
      </section>
    );
  }

  return (
    <section className="max-w-2xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.goodDayFallbackDay.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.goodDayFallbackDay.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.goodDayFallbackDay.description')}
        </p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">{t('tools.goodDayFallbackDay.goodDay.label')}</span>
            <p className="text-xs text-gray-1 mb-s2">
              {t('tools.goodDayFallbackDay.goodDay.hint')}
            </p>
            <textarea
              value={form.goodDay}
              onChange={(e) => setForm((prev) => ({ ...prev, goodDay: e.target.value }))}
              className="input-field mt-s2 min-h-32"
              data-testid="good-day-fallback-day-good"
              rows={5}
            />
          </label>
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">{t('tools.goodDayFallbackDay.fallbackDay.label')}</span>
            <p className="text-xs text-gray-1 mb-s2">
              {t('tools.goodDayFallbackDay.fallbackDay.hint')}
            </p>
            <textarea
              value={form.fallbackDay}
              onChange={(e) => setForm((prev) => ({ ...prev, fallbackDay: e.target.value }))}
              className="input-field mt-s2 min-h-32"
              data-testid="good-day-fallback-day-fallback"
              rows={5}
            />
          </label>
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">
              {t('tools.goodDayFallbackDay.reflection.label')}
              <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                {t('tools.goodDayFallbackDay.optional')}
              </span>
            </span>
            <textarea
              value={form.reflection}
              onChange={(e) => setForm((prev) => ({ ...prev, reflection: e.target.value }))}
              className="input-field mt-s2 min-h-20"
              data-testid="good-day-fallback-day-reflection"
              rows={3}
            />
          </label>
        </article>

        {submitError && (
          <p role="alert" className="form-error" data-testid="good-day-fallback-day-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p
            role="status"
            className="text-sm text-flame-deep"
            data-testid="good-day-fallback-day-saved-at"
          >
            ✓{' '}
            {t('tools.goodDayFallbackDay.savedAt', {
              when: savedAt.slice(0, 19).replace('T', ' '),
            })}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting
            ? t('tools.goodDayFallbackDay.submitting')
            : t('tools.goodDayFallbackDay.submit')}
        </button>
        <FinalizeWarning />
      </form>
    </section>
  );
}
